import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, AsOfPicker, fmtMoney0, fmtMoney2, fmtPct, fmtNum, todayIso } from "./_shared";

export default function FinanceRelationships() {
  const [groupBy, setGroupBy] = useState("attorney");
  const [asOf, setAsOf] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/finance/v2/relationships", { params: { group_by: groupBy, as_of: asOf }});
        setData(data);
      } catch (e) { toast.error("Load failed: " + formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [groupBy, asOf]);

  if (loading && !data) return <div className="p-10 text-[#6B7280]" data-testid="relationships-loading">Loading…</div>;
  if (!data) return null;

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto" data-testid="finance-relationships">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Finance V2 · preview</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0A2540]">Attorney & Firm Economics</h1>
          <div className="text-sm text-[#6B7280] mt-1.5">{data.rows.length} {groupBy === "attorney" ? "attorneys" : "firms"} · book contractual receivable {fmtMoney0(data.total_book_contractual_receivable)}</div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Group by</label>
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)} data-testid="relationships-group-by"
                    className="h-9 px-2 text-sm border border-[#E5E7EB] rounded-md bg-white">
              <option value="attorney">Attorney</option>
              <option value="firm">Firm</option>
            </select>
          </div>
          <AsOfPicker value={asOf} onChange={setAsOf} testid="relationships-as-of" />
        </div>
      </div>

      <Section title="Relationship rollup" category="mixed">
        <div className="overflow-auto rounded-md border border-[#E5E7EB] bg-white">
          <table className="w-full text-xs" data-testid="relationships-table">
            <thead className="bg-[#F9FAFB] sticky top-0">
              <tr className="text-left text-[#6B7280] uppercase tracking-wider">
                <th className="px-3 py-2 font-medium">{groupBy === "attorney" ? "Attorney" : "Firm"}</th>
                <th className="px-3 py-2 font-medium text-right">Loans</th>
                <th className="px-3 py-2 font-medium text-right">Repeat rate</th>
                <th className="px-3 py-2 font-medium text-right">Avg advance</th>
                <th className="px-3 py-2 font-medium text-right">Deployed lifetime</th>
                <th className="px-3 py-2 font-medium text-right">Outstanding</th>
                <th className="px-3 py-2 font-medium text-right">Contractual receivable</th>
                <th className="px-3 py-2 font-medium text-right">Concentration</th>
                <th className="px-3 py-2 font-medium text-right">Realized cases</th>
                <th className="px-3 py-2 font-medium text-right">Realized revenue</th>
                <th className="px-3 py-2 font-medium text-right">Realized loss</th>
                <th className="px-3 py-2 font-medium text-right">MOIC</th>
                <th className="px-3 py-2 font-medium text-right">Avg duration</th>
                <th className="px-3 py-2 font-medium">First → Last funding</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.key} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`rel-row-${r.key}`}>
                  <td className="px-3 py-2">
                    <div className="text-[#0A2540] font-medium">{r.display_name}</div>
                    <div className="text-[10px] text-[#9CA3AF]">
                      {r.firm ? `${r.firm}` : ""}
                      {r.firm && r.attorney_email ? " · " : ""}
                      {r.attorney_email}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.loans}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{fmtPct(r.repeat_funding_rate, 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney0(r.avg_advance)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney0(r.total_deployed_lifetime)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney0(r.outstanding_deployed)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#0066FF]">{fmtMoney0(r.contractual_receivable_open)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtPct(r.concentration_pct_of_open_receivable, 1)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.simple_realized_cases}
                    {r.unresolved_realized_cases > 0 && <span className="text-[10px] text-[#92400E] ml-1">(+{r.unresolved_realized_cases})</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#065F46]">{fmtMoney0(r.realized_funding_revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#991B1B]">{r.realized_principal_loss > 0 ? fmtMoney0(r.realized_principal_loss) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.realized_moic_simple != null ? `${r.realized_moic_simple.toFixed(2)}×` : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{r.avg_case_duration_days ?? "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[#6B7280]">{r.first_funding || "—"} → {r.last_funding || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-[#6B7280] mt-2">
          Realized cases = simple-cohort settled loans; <span className="text-[#92400E]">(+n)</span> = unresolved (complex or missing recorded amount).
          MOIC and revenue are simple-cohort only.
        </div>
      </Section>
    </div>
  );
}
